import {TOWN_REPORTS} from '../data/townReportNavigation';
import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
// src/features/townBudget/components/SummaryOfExpenditures.jsx

/**
 * @file SummaryOfExpenditures.jsx
 * @description Displays a collapsible summary table of expenditure and transfer categories
 * (Town, Debt Service & Transfers, Education) for the selected fiscal year.
 * Mirrors the structure of SummaryOfRevenues.jsx.
 *
 * Enhanced:
 * - Adds 3 line charts (Approved, Actual, Approved vs Actual)
 * - Adds pie chart for category breakdown (Town/Debt/Education)
 */

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { buildRevenueColumns } from "../../../budget/model/columns/columnBuilders.js";
import {
  applySectionRowOrder,
  createSectionChangeHandler,
  buildSingleSectionDisplayData,
  buildIntergovDisplayData,
} from "../utils/revenueTableUtils.js";
import { TOWN_SECTION_ORDER } from "../../../shared/constants/tableOrder.js";
import { fetchExpenditureSections } from "../data/fetchExpenditureSections.js";

// Charts UI (same library used elsewhere)
import { PieChartWithToggles } from "../../../shared/ui/chart/PieChartWithToggles.jsx";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles.jsx";

// ✅ Reuse the SAME summary utils used by SummaryOfRevenuesAndExpenditures
import {
  computeIncreaseFields,
  aggregateFromSections,
  rowHasAnyNumeric,
} from "../utils/summaryOfRevenuesAndExpendituresUtils.js";

function fyLabelFromEndYear(endYear) {
  if (!Number.isFinite(endYear)) return "";
  return `${endYear - 1}–${endYear}`;
}

function SummaryOfExpenditures({
  numericYears = [],
  availableYears = [],
  setData,
  isEditing,
}) {
  const [collapsed, setCollapsed] = useState(true);

  // Split initial loading vs updating so we don't unmount tables on year change
  const [loading, setLoading] = useState(false); // "no data yet"
  const [isUpdating, setIsUpdating] = useState(false); // "data already shown; swapping year"

  // Local year state: dropdown ONLY affects this component.
  const [tableYear, setTableYear] = useSharedFilter("SummaryOfExpenditures.jsx:SummaryOfExpenditures:tableYear", null);

  const [townSections, setTownSections] = useState([]);
  const [debtSections, setDebtSections] = useState([]);
  const [educationSections, setEducationSections] = useState([]);

  // ✅ filtered dropdown options (only complete years)
  const [validYears, setValidYears] = useState([]);

  // Cache results per year so switching back feels instant
  const yearCacheRef = useRef(new Map()); // Map<number, { townSections, debtSections, educationSections, raw }>

  const hasAnyData =
    townSections.length || debtSections.length || educationSections.length;

  // Initialize to latest available year.
  useEffect(() => {
    if (availableYears.length > 0) {
      setTableYear.initialize((prev) => {
        const latest = availableYears[availableYears.length - 1];
        return prev && availableYears.includes(prev) ? prev : latest;
      });
      return;
    }

    // Fallback: if no availableYears passed, derive from numericYears
    if (numericYears.length > 0) {
      const latest = Math.max(...numericYears);
      setTableYear.initialize((prev) => (typeof prev === "number" ? prev : latest));
    }
  }, [availableYears, numericYears]);

  const fallbackYear = new Date().getFullYear() + 1;
  const currentYear =
    tableYear ??
    (numericYears.length ? Math.max(...numericYears) : fallbackYear);

  const { columns } = useMemo(
    () => buildRevenueColumns(currentYear),
    [currentYear]
  );

  const yearKey = (offset, type) => `y${offset}_${type}`;

  // For “completeness”, require all base expenditure columns (except label)
  // Keys are stable: y2_adopted, y2_actual, y1_adopted, y0_adopted
  const requiredBaseKeys = useMemo(() => {
    const sampleYear =
      availableYears?.[availableYears.length - 1] ??
      new Date().getFullYear() + 1;

    const { baseColumns: sampleBase } = buildRevenueColumns(sampleYear);

    return (sampleBase || [])
      .map((c) => c?.key)
      .filter((k) => k && k !== "label");
  }, [availableYears]);

  const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

  // ---- Fetch + cache sections for a given year ----
  const fetchAndCacheYearSections = useCallback(async (year) => {
    const { baseColumns: yrBaseCols } = buildRevenueColumns(year);

    const [town, debtServiceAndTransfers, education] = await Promise.all([
      fetchExpenditureSections("town", yrBaseCols),
      fetchExpenditureSections("debtServiceAndTransfers", yrBaseCols),
      fetchExpenditureSections("education", yrBaseCols),
    ]);

    const next = {
      townSections: applySectionRowOrder(town.sections, "town"),
      debtSections: applySectionRowOrder(
        debtServiceAndTransfers.sections,
        "debtServiceAndTransfers"
      ),
      educationSections: applySectionRowOrder(education.sections, "education"),
      raw: { town, debtServiceAndTransfers, education },
    };

    yearCacheRef.current.set(year, next);
    return next;
  }, []);

  // ✅ Build GRAND TOTAL EXPENDITURES row using the SAME “only set if saw numeric” rule
  const computeGrandTotalRowForYear = useCallback((year, sectionsObj) => {
    const { columns: yearColumns } = buildRevenueColumns(year);

    const townTotal = aggregateFromSections(
      sectionsObj?.townSections || [],
      yearColumns,
      "TOTAL TOWN"
    );

    const debtTotal = aggregateFromSections(
      sectionsObj?.debtSections || [],
      yearColumns,
      "TOTAL DEBT SERVICE & TRANSFERS OUT"
    );

    const eduTotal = aggregateFromSections(
      sectionsObj?.educationSections || [],
      yearColumns,
      "TOTAL EDUCATION"
    );

    const pieces = [townTotal, debtTotal, eduTotal].filter((r) =>
      rowHasAnyNumeric(r, yearColumns)
    );

    if (!pieces.length) return null;

    const row = { label: "GRAND TOTAL EXPENDITURES AND TRANSFERS", bold: true };
    const yearCols = yearColumns.filter(
      (c) => !["label", "increase", "percentIncrease"].includes(c.key)
    );

    yearCols.forEach((col) => {
      let sum = 0;
      let hasVal = false;

      for (const r of pieces) {
        const v = r?.[col.key];
        if (typeof v === "number" && Number.isFinite(v)) {
          sum += v;
          hasVal = true;
        }
      }

      if (hasVal) row[col.key] = sum;
    });

    return computeIncreaseFields(row);
  }, []);

  // Build pie rows (Town sections + Debt sections). EXCLUDES Education.
  const computeTownDebtAggRowsForYear = useCallback((year, sectionsObj) => {
    const { columns: yearColumns } = buildRevenueColumns(year);

    const buildPerSectionAggRows = (sections, fallbackPrefix) => {
      const list = Array.isArray(sections) ? sections : [];
      const rows = [];

      list.forEach((section, idx) => {
        const label =
          section?.title ||
          section?.name ||
          section?.label ||
          section?.sectionTitle ||
          `${fallbackPrefix} ${idx + 1}`;

        // Aggregate ONLY this section by passing [section]
        const agg = aggregateFromSections([section], yearColumns, label);
        if (rowHasAnyNumeric(agg, yearColumns)) rows.push(agg);
      });

      return rows;
    };

    const townRows = buildPerSectionAggRows(
      sectionsObj?.townSections || [],
      "TOWN"
    );

    const debtRows = buildPerSectionAggRows(
      sectionsObj?.debtSections || [],
      "DEBT"
    );

    // Combine and return (no Education)
    return [...townRows, ...debtRows];
  }, []);

  const isYearComplete = useCallback(
    (year, sectionsObj) => {
      const grand = computeGrandTotalRowForYear(year, sectionsObj);
      if (!grand) return false;

      // ✅ strict rule: every required comparison column must be present as a finite number
      return requiredBaseKeys.every((k) => isFiniteNumber(grand?.[k]));
    },
    [requiredBaseKeys, computeGrandTotalRowForYear]
  );

  // ✅ compute validYears when expanded so dropdown only shows complete years
  useEffect(() => {
    if (collapsed) return;

    const sourceYears = availableYears.length
      ? availableYears
      : [...new Set(numericYears)]
          .filter((n) => Number.isFinite(n))
          .sort((a, b) => a - b);

    if (!sourceYears.length) {
      setValidYears([]);
      return;
    }

    let cancelled = false;

    (async () => {
      const nextValid = [];

      for (const y of sourceYears) {
        if (cancelled) return;

        // Use cache if present; otherwise fetch+cache
        let sectionsObj = yearCacheRef.current.get(y);
        if (!sectionsObj) {
          try {
            sectionsObj = await fetchAndCacheYearSections(y);
          } catch (e) {
            continue; // invalid
          }
        }

        try {
          if (isYearComplete(y, sectionsObj)) nextValid.push(y);
        } catch (e) {
          // invalid
        }
      }

      if (cancelled) return;

      setValidYears(nextValid);

      // If current selection is invalid, bump to latest valid year
      if (nextValid.length) {
        setTableYear.initialize((prev) =>
          prev && nextValid.includes(prev)
            ? prev
            : nextValid[nextValid.length - 1]
        );
      } else {
        // No valid years (rare) — fall back to latest available/source
        setTableYear.initialize((prev) => {
          const latest = sourceYears[sourceYears.length - 1];
          return prev && sourceYears.includes(prev) ? prev : latest;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    collapsed,
    availableYears,
    numericYears,
    fetchAndCacheYearSections,
    isYearComplete,
  ]);

  const yearOptionsToShow = useMemo(() => {
    if (validYears.length) return validYears;

    if (availableYears.length) return availableYears;

    return [...new Set(numericYears)]
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
  }, [validYears, availableYears, numericYears]);

  // ---- Load selected year into visible state (uses cache when possible) ----
  useEffect(() => {
    if (collapsed) return;
    if (!tableYear) return;

    // Serve cached result immediately (no spinner, no fetch)
    const cached = yearCacheRef.current.get(tableYear);
    if (cached) {
      setTownSections(cached.townSections);
      setDebtSections(cached.debtSections);
      setEducationSections(cached.educationSections);
      setLoading(false);
      setIsUpdating(false);

      if (typeof setData === "function" && cached.raw) {
        setData((prev) => ({
          ...prev,
          [tableYear]: { expendituresSections: cached.raw },
        }));
      }

      return;
    }

    if (hasAnyData) setIsUpdating(true);
    else setLoading(true);

    let cancelled = false;

    (async () => {
      try {
        const next = await fetchAndCacheYearSections(tableYear);
        if (cancelled) return;

        setTownSections(next.townSections);
        setDebtSections(next.debtSections);
        setEducationSections(next.educationSections);

        if (typeof setData === "function" && next.raw) {
          setData((prev) => ({
            ...prev,
            [tableYear]: { expendituresSections: next.raw },
          }));
        }

        setLoading(false);
        setIsUpdating(false);
      } catch (err) {
        console.error("[SummaryOfExpenditures] Error fetching summary:", err);
        if (!cancelled) {
          // IMPORTANT: do NOT clear rows on update failure; keep the last good view
          setLoading(false);
          setIsUpdating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collapsed, tableYear, hasAnyData, fetchAndCacheYearSections, setData]);

  const handleTownChange = createSectionChangeHandler(setTownSections);
  const handleDebtChange = createSectionChangeHandler(setDebtSections);
  const handleEducationChange =
    createSectionChangeHandler(setEducationSections);

  // ---- display data for each table ----
  const townDisplayData = useMemo(() => {
    const base = buildIntergovDisplayData(townSections, columns);
    return base.map((row) =>
      typeof row.label === "string" &&
      row.label.toUpperCase().startsWith("TOTAL INTERGOVERNMENTAL")
        ? { ...row, label: "TOTAL TOWN" }
        : row
    );
  }, [townSections, columns]);

  const debtDisplayData = useMemo(() => {
    const base = buildIntergovDisplayData(debtSections, columns);
    return base.map((row) =>
      typeof row.label === "string" &&
      row.label.toUpperCase().startsWith("TOTAL INTERGOVERNMENTAL")
        ? { ...row, label: "TOTAL DEBT SERVICE & TRANSFERS OUT" }
        : row
    );
  }, [debtSections, columns]);

  const educationDisplayData = useMemo(
    () =>
      buildSingleSectionDisplayData(
        educationSections,
        columns,
        "TOTAL EDUCATION"
      ),
    [educationSections, columns]
  );

  // ---- GRAND TOTAL EXPENDITURES & TRANSFERS (display) ----
  const grandTotalRow = useMemo(() => {
    const totalRows = [
      ...townDisplayData,
      ...debtDisplayData,
      ...educationDisplayData,
    ].filter((row) => {
      const label = (row.label || "").toUpperCase();
      return (
        label === "TOTAL TOWN" ||
        label === "TOTAL DEBT SERVICE & TRANSFERS OUT" ||
        label === "TOTAL EDUCATION"
      );
    });

    if (totalRows.length === 0) return null;

    const total = { label: "GRAND TOTAL EXPENDITURES AND TRANSFERS" };

    columns.forEach((col) => {
      const key = col.key;
      if (key === "label" || key === "increase" || key === "percentIncrease")
        return;

      const sawAny = totalRows.some((r) => isFiniteNumber(r?.[key]));
      if (!sawAny) return;

      const sum = totalRows.reduce((acc, row) => {
        const v = row[key];
        return typeof v === "number" ? acc + v : acc;
      }, 0);

      total[key] = sum;
    });

    const prev = total.y1_adopted;
    const curr = total.y0_adopted;

    total.increase =
      typeof prev === "number" && typeof curr === "number" ? curr - prev : null;

    total.percentIncrease =
      typeof prev === "number" && prev !== 0 && typeof curr === "number"
        ? ((curr - prev) / prev) * 100
        : null;

    return total;
  }, [townDisplayData, debtDisplayData, educationDisplayData, columns]);

  // ---------------------------
  // Charts + Pie UI state
  // ---------------------------
  const [showApprovedTotalsChart, setShowApprovedTotalsChart] = useState(false);
  const [showActualTotalsChart, setShowActualTotalsChart] = useState(false);
  const [showDiffTotalsChart, setShowDiffTotalsChart] = useState(false);

  const [showExpenditurePie, setShowExpenditurePie] = useState(false);
  const [pieColKey, setPieColKey] = useSharedFilter("SummaryOfExpenditures.jsx:SummaryOfExpenditures:pieColKey", "y0_adopted");

  const [isChartUpdating, setIsChartUpdating] = useState(false);

  // Ensure pieColKey stays valid for the currently-built columns
  const pieColOptions = useMemo(() => {
    const numericCols = (columns || []).filter(
      (c) => c?.key && !["label", "increase", "percentIncrease"].includes(c.key)
    );

    return numericCols.map((c) => ({
      key: c.key,
      label: typeof c.label === "string" ? c.label : c.key,
    }));
  }, [columns]);

  useEffect(() => {
    if (!pieColOptions.length) return;

    setPieColKey.initialize((prev) => {
      const preferred =
        pieColOptions.find((o) => o.key === "y0_adopted")?.key ||
        pieColOptions[pieColOptions.length - 1].key;

      if (prev && pieColOptions.some((o) => o.key === prev)) return prev;
      return preferred;
    });
  }, [pieColOptions]);

  // Warm cache for charts when any chart is opened
  const ensureYearsCached = useCallback(
    async (yearsToEnsure) => {
      const list = Array.isArray(yearsToEnsure) ? yearsToEnsure : [];
      if (!list.length) return;

      const missing = list.filter((y) => !yearCacheRef.current.get(y));
      if (!missing.length) return;

      setIsChartUpdating(true);
      try {
        for (const y of missing) {
          await fetchAndCacheYearSections(y);
        }
      } finally {
        setIsChartUpdating(false);
      }
    },
    [fetchAndCacheYearSections]
  );

  const baseYearsForCharts = useMemo(
    () => (yearOptionsToShow.length ? yearOptionsToShow : availableYears),
    [yearOptionsToShow, availableYears]
  );

  useEffect(() => {
    if (collapsed) return;

    const wantsAnyChart =
      showApprovedTotalsChart || showActualTotalsChart || showDiffTotalsChart;

    if (!wantsAnyChart) return;

    // For actual anchoring, we may need (Y+2) docs available
    const plus2Years = baseYearsForCharts.map((y) => y + 2);
    const toEnsure = Array.from(
      new Set([...baseYearsForCharts, ...plus2Years])
    );

    ensureYearsCached(toEnsure);
  }, [
    collapsed,
    showApprovedTotalsChart,
    showActualTotalsChart,
    showDiffTotalsChart,
    baseYearsForCharts,
    ensureYearsCached,
  ]);

  // Approved totals points (GRAND TOTAL EXPENDITURES.y0_adopted) by FY end year
  const expenditureApprovedTotalsPoints = useMemo(() => {
    const baseYears = baseYearsForCharts;
    if (!baseYears.length) return [];

    const pts = [];
    baseYears.forEach((endYear) => {
      const sectionsObj = yearCacheRef.current.get(endYear);
      if (!sectionsObj) return;

      const totalRow = computeGrandTotalRowForYear(endYear, sectionsObj);
      const v = totalRow?.y0_adopted;

      if (typeof v === "number" && Number.isFinite(v))
        pts.push({ year: endYear, total: v });
    });

    pts.sort((a, b) => a.year - b.year);
    return pts;
  }, [baseYearsForCharts, computeGrandTotalRowForYear]);

  // Actual totals points:
  // actual for FY endYear is stored in (endYear+2).y2_actual (same “Y+2 / y2_actual” anchoring pattern)
  const expenditureActualTotalsPoints = useMemo(() => {
    const baseYears = baseYearsForCharts;
    if (!baseYears.length) return [];

    const pts = [];
    baseYears.forEach((fyEndYear) => {
      const anchorYear = fyEndYear + 2;
      const sectionsObj = yearCacheRef.current.get(anchorYear);
      if (!sectionsObj) return;

      const totalRow = computeGrandTotalRowForYear(anchorYear, sectionsObj);
      const v = totalRow?.y2_actual;

      if (typeof v === "number" && Number.isFinite(v))
        pts.push({ year: fyEndYear, total: v });
    });

    pts.sort((a, b) => a.year - b.year);
    return pts;
  }, [baseYearsForCharts, computeGrandTotalRowForYear]);

  const expenditureApprovedTotalsChart = useMemo(() => {
    if (!expenditureApprovedTotalsPoints.length)
      return { labels: [], series: [] };

    const labels = expenditureApprovedTotalsPoints.map((p) =>
      fyLabelFromEndYear(p.year)
    );
    const data = expenditureApprovedTotalsPoints.map((p) => p.total);

    return {
      labels,
      series: [
        {
          key: "totalApprovedExpenditures",
          label: "Total Approved Expenditures",
          data,
          group: "budget",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
    };
  }, [expenditureApprovedTotalsPoints]);

  const expenditureActualTotalsChart = useMemo(() => {
    if (!expenditureActualTotalsPoints.length)
      return { labels: [], series: [] };

    const labels = expenditureActualTotalsPoints.map((p) =>
      fyLabelFromEndYear(p.year)
    );
    const data = expenditureActualTotalsPoints.map((p) => p.total);

    return {
      labels,
      series: [
        {
          key: "totalActualExpenditures",
          label: "Total Actual Expenditures",
          data,
          group: "spending",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
    };
  }, [expenditureActualTotalsPoints]);

  const expenditureDiffTotalsChart = useMemo(() => {
    if (
      !expenditureApprovedTotalsPoints.length ||
      !expenditureActualTotalsPoints.length
    ) {
      return { labels: [], series: [], groups: [] };
    }

    const approvedByYear = new Map(
      expenditureApprovedTotalsPoints.map((p) => [p.year, p.total])
    );
    const actualByYear = new Map(
      expenditureActualTotalsPoints.map((p) => [p.year, p.total])
    );

    const commonYears = [...approvedByYear.keys()]
      .filter((y) => actualByYear.has(y))
      .sort((a, b) => a - b);

    if (!commonYears.length) return { labels: [], series: [], groups: [] };

    const labels = commonYears.map((y) => fyLabelFromEndYear(y));

    const series = [
      {
        key: "totalApprovedExpenditures",
        label: "Total Approved Expenditures",
        data: commonYears.map((y) => approvedByYear.get(y) ?? null),
        group: "budget",
        defaultVisible: true,
        valueType: "currency",
      },
      {
        key: "totalActualExpenditures",
        label: "Total Actual Expenditures",
        data: commonYears.map((y) => actualByYear.get(y) ?? null),
        group: "spending",
        defaultVisible: true,
        valueType: "currency",
      },
    ];

    const groups = [
      {
        key: "totalExpenditures",
        label: "Total Expenditures",
        groupKey: "budget",
        seriesKeys: ["totalApprovedExpenditures", "totalActualExpenditures"],
        defaultVisible: true,
      },
    ];

    return { labels, series, groups };
  }, [expenditureApprovedTotalsPoints, expenditureActualTotalsPoints]);

  const expenditurePieItems = useMemo(() => {
    if (!hasAnyData) return [];
    if (!pieColKey) return [];
    if (!tableYear) return [];

    const sectionsObj = yearCacheRef.current.get(tableYear);
    if (!sectionsObj) return [];

    // ✅ Town + Debt sections only (no Education)
    const rows = computeTownDebtAggRowsForYear(tableYear, sectionsObj);

    const items = [];

    const addSlice = (label, value, keyHint) => {
      if (typeof value !== "number" || !Number.isFinite(value) || value === 0)
        return;

      const safeKey =
        String(keyHint || label || "")
          .trim()
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_]/g, "") || `slice_${items.length + 1}`;

      items.push({
        key: safeKey,
        label,
        value,
        valueType: "currency",
        group: "other",
      });
    };

    rows.forEach((r, idx) => {
      const lbl = String(r?.label || "").trim();
      if (!lbl) return;
      addSlice(lbl, r?.[pieColKey], `townDebt_${idx}_${lbl}`);
    });

    items.sort((a, b) => (b.value || 0) - (a.value || 0));
    return items;
  }, [hasAnyData, pieColKey, tableYear, computeTownDebtAggRowsForYear]);

  return (
    <TableSearchScope>
    <div className="bg-base-100 shadow-md rounded-lg p-2 mt-6 border border-gray-300">
      <HeaderToggleButton reportNavigation title={TOWN_REPORTS[2].title} controlsId="town-expenditure-summary"
        isOpen={!collapsed} onToggle={() => setCollapsed(prev => !prev)} />

      {!collapsed && (
        <div id="town-expenditure-summary" className="bg-white p-4 space-y-6">
          {loading && !hasAnyData ? (
            <div className="text-sm text-gray-500">
              Loading expenditure summary...
            </div>
          ) : (
            <>
              {/* TOWN header row with Current Year on same axis */}
              <div className="flex flex-wrap gap-2 items-center justify-between rounded border border-gray-200 bg-gray-100 px-2 py-1">
                <h3 className="text-lg font-semibold">TOWN</h3>

                {yearOptionsToShow.length > 0 && (
                  <div className="ml-auto flex flex-wrap items-center justify-end gap-2 text-xs">
                    <SectionTableSearch />
                    <span className="font-semibold text-gray-700">
                      Current Year
                    </span>
                    <select
                      className="border rounded px-2 py-1 text-xs bg-white"
                      value={tableYear || ""}
                      onChange={(e) =>
                        setTableYear(
                          e.target.value ? Number(e.target.value) : tableYear
                        )
                      }
                    >
                      {yearOptionsToShow.map((y) => (
                        <option key={y} value={y}>
                          {fyLabelFromEndYear(y)}
                        </option>
                      ))}
                    </select>

                    {isUpdating && (
                      <span className="text-gray-500" aria-live="polite">
                        Updating…
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* TOWN table: title removed because we render it above */}
              <CommonTable
                title={null}
                columns={columns}
                data={townDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#d5e7f7]"
                onChange={handleTownChange}
                showAggregateTotal={false}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
                sectionOrder={TOWN_SECTION_ORDER}
              />

              {/* DEBT SERVICE & TRANSFERS OUT */}
              <CommonTable
                title="DEBT SERVICE & TRANSFERS OUT"
                columns={columns}
                data={debtDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#d5e7f7]"
                onChange={handleDebtChange}
                showAggregateTotal={false}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
              />

              {/* EDUCATION */}
              <CommonTable
                title="EDUCATION"
                columns={columns}
                data={educationDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#d5e7f7]"
                onChange={handleEducationChange}
                showAggregateTotal={false}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
              />

              {/* GRAND TOTAL EXPENDITURES & TRANSFERS */}
              {grandTotalRow && (
                <CommonTable
                  title=""
                  columns={columns}
                  data={[grandTotalRow]}
                  isEditing={false}
                  headerColor="bg-[#d5e7f7]"
                  onChange={() => {}}
                  showAggregateTotal={false}
                  dataKey={yearKey(0, "adopted")}
                />
              )}

              {/* Charts */}
              <div className="space-y-6 pt-2">
                {expenditureApprovedTotalsChart.labels.length > 0 &&
                  expenditureApprovedTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showApprovedTotalsChart}
                        onToggle={() =>
                          setShowApprovedTotalsChart((prev) => !prev)
                        }
                        title="Approved Expenditures"
                        ariaLabel="Toggle approved expenditures chart"
                        controlsId="expenditures-totals-approved-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showApprovedTotalsChart && (
                        <div id="expenditures-totals-approved-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart shows total approved expenditures (GRAND
                            TOTAL EXPENDITURES) by fiscal year.
                          </p>

                          <LineChartWithToggles
                            labels={expenditureApprovedTotalsChart.labels}
                            series={expenditureApprovedTotalsChart.series}
                            showTotalWhenNoneSelected={true}
                            totalLabel="Total Approved Expenditures"
                            showSeriesToggles={true}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {expenditureActualTotalsChart.labels.length > 0 &&
                  expenditureActualTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showActualTotalsChart}
                        onToggle={() =>
                          setShowActualTotalsChart((prev) => !prev)
                        }
                        title="Actual Expenditures"
                        ariaLabel="Toggle actual expenditures chart"
                        controlsId="expenditures-totals-actual-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showActualTotalsChart && (
                        <div id="expenditures-totals-actual-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart shows total actual expenditures by fiscal
                            year (using the same Y+2 / y2_actual anchoring used
                            elsewhere).
                          </p>

                          <LineChartWithToggles
                            labels={expenditureActualTotalsChart.labels}
                            series={expenditureActualTotalsChart.series}
                            showTotalWhenNoneSelected={true}
                            totalLabel="Total Actual Expenditures"
                            showSeriesToggles={true}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {expenditureDiffTotalsChart.labels.length > 0 &&
                  expenditureDiffTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showDiffTotalsChart}
                        onToggle={() => setShowDiffTotalsChart((prev) => !prev)}
                        title="Actual Spending vs Approved Budget Expenditures"
                        ariaLabel="Toggle actual vs approved expenditures chart"
                        controlsId="expenditures-totals-diff-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showDiffTotalsChart && (
                        <div id="expenditures-totals-diff-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart compares total actual vs total approved
                            expenditures for years where both are available.
                          </p>

                          <LineChartWithToggles
                            labels={expenditureDiffTotalsChart.labels}
                            series={expenditureDiffTotalsChart.series}
                            groups={expenditureDiffTotalsChart.groups}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {/* Expenditure Breakdown Pie */}
                <div className="space-y-1">
                  <HeaderToggleButton
                    isOpen={showExpenditurePie}
                    onToggle={() => setShowExpenditurePie((p) => !p)}
                    title="Breakdown of Town Expenditures"
                    ariaLabel="Toggle breakdown of expenditures"
                    controlsId="expenditures-breakdown-pie"
                    rightSlot={
                      isChartUpdating ? (
                        <span
                          className="text-xs text-gray-500 pr-2"
                          aria-live="polite"
                        >
                          Updating…
                        </span>
                      ) : null
                    }
                  />

                  {showExpenditurePie && (
                    <div id="expenditures-breakdown-pie">
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                        <p className="text-xs text-gray-600">
                          This pie shows how Town and Debt Service & Transfers
                          Out expenditures are split across major sections
                          (e.g., General Government, Community Development,
                          etc.) for the selected column.
                        </p>

                        {pieColOptions.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-semibold">Column</span>
                            <select
                              className="border rounded px-2 py-1 text-xs bg-white"
                              value={pieColKey || ""}
                              onChange={(e) => setPieColKey(e.target.value)}
                            >
                              {pieColOptions.map((o) => (
                                <option key={o.key} value={o.key}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {expenditurePieItems.length > 0 ? (
                        <PieChartWithToggles
                          title={null}
                          items={expenditurePieItems}
                          percentOf="all"
                          showLegend={false}
                          showSeriesToggles={false}
                          showChartTypeSelector={false}
                          aggregateOthers={false}
                          includeZeroValues={true}
                          showLegendTable={true}
                          legendTablePlacement="right"
                          legendTableMaxHeight="420px"
                          enableHoverPop={true}
                          hoverPopOffset={12}
                          sliceOrder="valueDesc"
                        />
                      ) : (
                        <div className="text-sm text-gray-500">
                          No expenditure data available for the selected column.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
    </TableSearchScope>
  );
}

export default SummaryOfExpenditures;
